import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/Toast';
import { useConfirm } from '../../components/Modal';
import { useSocket } from '../../contexts/SocketContext';
import { QrCode, CheckCircle2, RefreshCw, ArrowLeft, Download, Plus, Trash2, Image } from 'lucide-react';
import api from '../../utils/api';
import { saveImageToAlbum } from '../../utils/saveImage';
import { timeAgo, formatBJ } from '../../utils/datetime';
import AdminNotificationBell from '../../components/AdminNotificationBell';
import TabBar from '../../components/TabBar';
import BizSwitch from '../../components/BizSwitch';
import PuzzleMaterialManage from '../../components/PuzzleMaterialManage';
import { useBizMode } from '../../utils/bizMode';

// ─── 通用多图管理卡片（问候图 / 回访图）─────────────────────────────────────
function MultiImageManageCard({
  title,
  images,
  apiGetPath,
  apiPostPath,   // (no) => `/admin/xxx/${no}`
  apiDeletePath, // (no) => `/admin/xxx/${no}`
  broadcastEvent,
  onRefresh,
}) {
  const [currentNo, setCurrentNo] = useState(null);
  const [savingNo, setSavingNo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOverNo, setDragOverNo] = useState(null);
  const { showToast } = useToast();
  const confirm = useConfirm();

  useEffect(() => {
    if (images.length === 0) { setCurrentNo(null); return; }
    if (!images.some(img => img.img_no === currentNo)) setCurrentNo(images[0].img_no);
  }, [images, currentNo]);

  const current = images.find(img => img.img_no === currentNo) || null;

  // ── 保存（下载）指定图 ──
  const handleSave = async (img) => {
    if (!img) return;
    setSavingNo(img.img_no);
    try {
      await saveImageToAlbum(img.image_data, `${title.replace(/\s/g, '')}${img.img_no}.png`);
      showToast('已打开保存面板', 'success');
    } catch {
      showToast('保存失败，请长按图片保存', 'error');
    } finally { setSavingNo(null); }
  };

  // ── 上传核心 ──
  const uploadFile = useCallback((file, no) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { showToast('请选择图片文件', 'error'); return; }
    if (file.size > 5 * 1024 * 1024) { showToast('图片不能超过 5MB', 'error'); return; }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      setUploading(true);
      try {
        const res = await api.post(apiPostPath(no), { imageData: ev.target.result });
        showToast(res.data.message || '保存成功', 'success');
        await onRefresh();
        setCurrentNo(no);
      } catch (err) {
        showToast(err.response?.data?.error || '上传失败', 'error');
      } finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
  }, [apiPostPath, onRefresh, showToast]);

  const handleUpload = (e, no) => { uploadFile(e?.target?.files?.[0], no); };

  const handleDragOver = (e, no) => { e.preventDefault(); e.stopPropagation(); if (!uploading) setDragOverNo(no); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragOverNo(null); };
  const handleDrop = (e, no) => {
    e.preventDefault(); e.stopPropagation(); setDragOverNo(null);
    if (uploading) return;
    uploadFile(e.dataTransfer?.files?.[0], no);
  };

  // ── 新增图 ──
  const handleAdd = () => {
    const nextNo = images.length ? Math.max(...images.map(img => img.img_no)) + 1 : 1;
    if (nextNo > 20) { showToast(`最多支持 20 张${title}`, 'error'); return; }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => { const file = e.target.files?.[0]; if (file) uploadFile(file, nextNo); };
    input.click();
  };

  // ── 删除指定图 ──
  const handleDelete = async (no) => {
    const ok = await confirm(`确认删除${title} ${no}？`, { detail: '删除后不可恢复。', danger: true });
    if (!ok) return;
    try {
      await api.delete(apiDeletePath(no));
      showToast('已删除', 'success');
      await onRefresh();
    } catch (err) {
      showToast(err.response?.data?.error || '删除失败', 'error');
    }
  };

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      {/* 顶部分页 tabs + 新增按钮 */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 15, fontWeight: 700, marginRight: 4 }}>{title}</span>
        {images.map(img => (
          <button
            key={img.img_no}
            onClick={() => setCurrentNo(img.img_no)}
            style={{
              padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600,
              border: `1.5px solid ${currentNo === img.img_no ? 'var(--c-primary)' : 'var(--c-border)'}`,
              background: currentNo === img.img_no ? 'var(--c-primary)' : 'var(--c-bg)',
              color: currentNo === img.img_no ? '#fff' : 'var(--c-text-secondary)',
              cursor: 'pointer',
            }}
          >
            图{img.img_no}
          </button>
        ))}
        <button
          onClick={handleAdd}
          style={{
            padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
            border: '1.5px dashed var(--c-primary)', background: 'transparent',
            color: 'var(--c-primary)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          <Plus size={14} /> 新增
        </button>
      </div>

      <div style={{ borderTop: '1px solid var(--c-border)', paddingTop: 14 }}>
        {images.length === 0 ? (
          /* 空状态：支持拖拽上传为 图1 */
          <div
            className="empty-state"
            onDragOver={e => handleDragOver(e, 1)}
            onDragLeave={handleDragLeave}
            onDrop={e => handleDrop(e, 1)}
            style={{
              padding: 20,
              border: dragOverNo === 1 ? '2px dashed var(--c-primary)' : 'none',
              borderRadius: 12,
            }}
          >
            <div className="empty-icon"><Image size={40} color="var(--c-text-secondary)" strokeWidth={1.2} /></div>
            <p>{dragOverNo === 1 ? `松开即可上传为${title} 1` : `尚未设置${title}，点击「新增」添加，或直接拖入图片`}</p>
          </div>
        ) : current ? (
          <div style={{ textAlign: 'center' }}>
            {/* 图片标题行 + 删除按钮 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 700 }}>{title} {current.img_no}</span>
              <button
                onClick={() => handleDelete(current.img_no)}
                style={{
                  background: 'none', border: 'none', color: 'var(--c-danger)',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13,
                }}
              >
                <Trash2 size={14} /> 删除此图
              </button>
            </div>

            <img
              src={current.image_data}
              alt={`${title}${current.img_no}`}
              style={{ display: 'block', margin: '0 auto 14px', maxWidth: '100%', maxHeight: 280, borderRadius: 10 }}
              onError={e => { e.target.style.display = 'none'; }}
            />

            {/* 保存按钮 */}
            <button
              onClick={() => handleSave(current)}
              disabled={savingNo === current.img_no}
              className="btn btn-outline"
              style={{ marginBottom: 10, width: '100%', height: 44, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            >
              <Download size={16} />
              {savingNo === current.img_no ? '保存中...' : `保存${title}`}
            </button>

            {/* 上传/更换按钮（支持点击 + 拖拽） */}
            <label
              className="btn btn-primary"
              onDragOver={e => handleDragOver(e, current.img_no)}
              onDragLeave={handleDragLeave}
              onDrop={e => handleDrop(e, current.img_no)}
              style={{
                cursor: uploading ? 'not-allowed' : 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                width: '100%', height: 44, fontSize: 15, borderRadius: 10,
                pointerEvents: uploading ? 'none' : 'auto',
                opacity: uploading ? 0.6 : 1,
                outline: dragOverNo === current.img_no ? '2px dashed #fff' : 'none',
                outlineOffset: -4,
              }}
            >
              {uploading
                ? '上传中...'
                : dragOverNo === current.img_no
                ? '松开即可上传'
                : `上传/更换${title} ${current.img_no}（点击或拖入图片）`}
              <input
                type="file" accept="image/*"
                onChange={e => handleUpload(e, current.img_no)}
                style={{ display: 'none' }}
                disabled={uploading}
              />
            </label>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ─── 管理员主页面 ─────────────────────────────────────────────────────────────
export default function QRCodeManagePage() {
  const navigate = useNavigate();
  const [bizMode] = useBizMode(); // 排五 / 证券
  const [codes, setCodes] = useState([]);
  const [currentNo, setCurrentNo] = useState(1);
  const [updating, setUpdating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingNo, setSavingNo] = useState(null);
  const [greetings, setGreetings] = useState([]);
  const [revisits, setRevisits] = useState([]);
  const { showToast } = useToast();
  const confirm = useConfirm();
  const { subscribe } = useSocket();
  const [relativeTime, setRelativeTime] = useState('');

  const fetchQRs = useCallback(() => {
    api.get('/admin/qrcodes').then(res => {
      setCodes(res.data.codes || []);
      setUpdating(false);
    }).catch(() => {});
  }, []);

  const fetchGreetings = useCallback(() => {
    api.get('/admin/greeting-images').then(r => setGreetings(r.data.images || [])).catch(() => {});
  }, []);

  const fetchRevisits = useCallback(() => {
    api.get('/admin/revisit-images').then(r => setRevisits(r.data.images || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchQRs();
    fetchGreetings();
    fetchRevisits();
  }, [fetchQRs, fetchGreetings, fetchRevisits]);

  useEffect(() => {
    if (codes.length === 0) return;
    if (!codes.some(c => c.code_no === currentNo)) setCurrentNo(codes[0].code_no);
  }, [codes, currentNo]);

  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => {
      if (event === 'qrcode_updated') { setUpdating(true); setTimeout(() => fetchQRs(), 300); }
      if (event === 'greeting_images_updated') fetchGreetings();
      if (event === 'revisit_images_updated') fetchRevisits();
    });
  }, [subscribe, fetchQRs, fetchGreetings, fetchRevisits]);

  const currentQR = codes.find(c => c.code_no === currentNo) || null;

  const handleSaveQR = async (no) => {
    const qr = codes.find(c => c.code_no === no);
    if (!qr) return;
    setSavingNo(no);
    try {
      await saveImageToAlbum(qr.image_data, `qrcode${no}.png`);
      showToast('已打开保存面板', 'success');
    } catch {
      showToast('保存失败，请长按二维码图片保存', 'error');
    } finally { setSavingNo(null); }
  };

  const uploadFile = useCallback((file, no) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { showToast('请选择图片文件', 'error'); return; }
    if (file.size > 5 * 1024 * 1024) { showToast('图片不能超过 5MB', 'error'); return; }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      setLoading(true);
      try {
        const res = await api.post(`/admin/qrcode/${no}`, { imageData: ev.target.result });
        showToast(res.data.message || '保存成功', 'success');
        await fetchQRs();
        setCurrentNo(no);
      } catch (err) {
        showToast(err.response?.data?.error || '上传失败', 'error');
      } finally { setLoading(false); }
    };
    reader.readAsDataURL(file);
  }, [fetchQRs, showToast]);

  const handleUpload = (e, no) => { uploadFile(e?.target?.files?.[0], no); };

  const [dragOverNo, setDragOverNo] = useState(null);
  const handleDragOver = (e, no) => { e.preventDefault(); e.stopPropagation(); if (!loading) setDragOverNo(no); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragOverNo(null); };
  const handleDrop = (e, no) => {
    e.preventDefault(); e.stopPropagation(); setDragOverNo(null);
    if (loading) return;
    uploadFile(e.dataTransfer?.files?.[0], no);
  };

  const handleAdd = () => {
    const nextNo = codes.length ? Math.max(...codes.map(c => c.code_no)) + 1 : 1;
    if (nextNo > 20) { showToast('最多支持 20 张二维码', 'error'); return; }
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*';
    input.onchange = (e) => { const file = e.target.files?.[0]; if (file) uploadFile(file, nextNo); };
    input.click();
  };

  const handleDelete = async (no) => {
    const ok = await confirm(`确认删除码${no}？`, { detail: '删除后不可恢复。', danger: true });
    if (!ok) return;
    try {
      const res = await api.delete(`/admin/qrcode/${no}`);
      showToast(res.data.message || '已删除', 'success');
      const next = codes.filter(c => c.code_no !== no);
      setCodes(next);
      if (currentNo === no) setCurrentNo(next.length ? next[0].code_no : 1);
      setUpdating(false);
    } catch (err) {
      showToast(err.response?.data?.error || '删除失败', 'error');
    }
  };

  useEffect(() => {
    const ci = codes.find(c => c.code_no === currentNo);
    if (ci?.created_at) {
      setRelativeTime(timeAgo(ci.created_at));
      const timer = setInterval(() => setRelativeTime(timeAgo(ci.created_at)), 60000);
      return () => clearInterval(timer);
    }
    setRelativeTime('');
  }, [codes, currentNo]);

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <button onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', color: 'var(--c-primary)' }}>
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <QrCode size={16} /> 获客素材管理
          </span>
        </span>
        <AdminNotificationBell />
      </div>

      <div style={{ padding: '12px 16px' }}>

        {/* 排五 / 证券 业务切换 */}
        <BizSwitch style={{ marginBottom: 12 }} />

        {bizMode === 'puzzle' ? <PuzzleMaterialManage /> : (<>

        {/* ── 问候图管理卡片 ── */}
        <MultiImageManageCard
          title="问候图"
          images={greetings}
          apiPostPath={(no) => `/admin/greeting-image/${no}`}
          apiDeletePath={(no) => `/admin/greeting-image/${no}`}
          onRefresh={fetchGreetings}
        />

        {/* ── 二维码管理卡片（原有） ── */}
        <div className="card" style={{ marginBottom: 12 }}>
          {/* 顶部 tabs */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 15, fontWeight: 700, marginRight: 4 }}>二维码</span>
            {codes.map(c => (
              <button
                key={c.code_no}
                onClick={() => setCurrentNo(c.code_no)}
                style={{
                  padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                  border: `1.5px solid ${currentNo === c.code_no ? 'var(--c-primary)' : 'var(--c-border)'}`,
                  background: currentNo === c.code_no ? 'var(--c-primary)' : 'var(--c-bg)',
                  color: currentNo === c.code_no ? '#fff' : 'var(--c-text-secondary)',
                  cursor: 'pointer',
                }}
              >
                码{c.code_no}
              </button>
            ))}
            <button
              onClick={handleAdd}
              style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                border: '1.5px dashed var(--c-primary)', background: 'transparent',
                color: 'var(--c-primary)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <Plus size={14} /> 新增
            </button>
          </div>

          <div style={{ borderTop: '1px solid var(--c-border)', paddingTop: 14 }}>
            {codes.length === 0 ? (
              <div
                className="empty-state"
                onDragOver={e => handleDragOver(e, 1)}
                onDragLeave={handleDragLeave}
                onDrop={e => handleDrop(e, 1)}
                style={{ padding: 20, border: dragOverNo === 1 ? '2px dashed var(--c-primary)' : 'none', borderRadius: 12 }}
              >
                <div className="empty-icon"><QrCode size={40} color="var(--c-text-secondary)" strokeWidth={1.2} /></div>
                <p>{dragOverNo === 1 ? '松开即可上传为码1' : '尚未设置二维码，点击「新增」添加码1，或直接拖入图片'}</p>
              </div>
            ) : currentQR ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: 14, fontWeight: 700 }}>码{currentQR.code_no}</span>
                  <button onClick={() => handleDelete(currentQR.code_no)}
                    style={{ background: 'none', border: 'none', color: 'var(--c-danger)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                    <Trash2 size={14} /> 删除此码
                  </button>
                </div>

                <img src={currentQR.image_data} alt={`码${currentQR.code_no}`}
                  style={{ display: 'block', margin: '0 auto 14px', maxWidth: '100%', maxHeight: 260, borderRadius: 10 }}
                  onError={e => { e.target.style.display = 'none'; }} />

                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
                  {updating ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: 'rgba(255,149,0,0.1)', color: '#b07000', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
                      <RefreshCw size={13} /> 正在更新...
                    </span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: 'rgba(52,199,89,0.1)', color: 'var(--c-success)', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
                      <CheckCircle2 size={13} /> 当前为最新
                    </span>
                  )}
                </div>

                {currentQR.created_at && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 14, color: 'var(--c-text-secondary)', marginBottom: 2 }}>{relativeTime || timeAgo(currentQR.created_at)}</div>
                    <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)' }}>{formatBJ(currentQR.created_at)}</div>
                  </div>
                )}

                <button onClick={() => handleSaveQR(currentQR.code_no)} disabled={savingNo === currentQR.code_no}
                  className="btn btn-outline"
                  style={{ marginBottom: 10, width: '100%', height: 44, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Download size={16} /> {savingNo === currentQR.code_no ? '保存中...' : '保存二维码'}
                </button>

                <label
                  className="btn btn-primary"
                  onDragOver={e => handleDragOver(e, currentQR.code_no)}
                  onDragLeave={handleDragLeave}
                  onDrop={e => handleDrop(e, currentQR.code_no)}
                  style={{
                    cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    width: '100%', height: 44, fontSize: 15, borderRadius: 10,
                    pointerEvents: loading ? 'none' : 'auto',
                    opacity: loading ? 0.6 : 1,
                    outline: dragOverNo === currentQR.code_no ? '2px dashed #fff' : 'none',
                    outlineOffset: -4,
                  }}
                >
                  {loading ? '上传中...' : dragOverNo === currentQR.code_no ? '松开即可上传' : `更换码${currentQR.code_no}（点击或拖入图片）`}
                  <input type="file" accept="image/*" onChange={e => handleUpload(e, currentQR.code_no)} style={{ display: 'none' }} disabled={loading} />
                </label>
              </div>
            ) : null}
          </div>
        </div>

        {/* ── 回访图管理卡片 ── */}
        <MultiImageManageCard
          title="回访图"
          images={revisits}
          apiPostPath={(no) => `/admin/revisit-image/${no}`}
          apiDeletePath={(no) => `/admin/revisit-image/${no}`}
          onRefresh={fetchRevisits}
        />
        </>)}
      </div>

      <TabBar mode="admin" />
    </div>
  );
}
